import { Component, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

interface CalendarEvent {
  id: number;
  title: string;
  date: string;
  type: 'academic' | 'finance' | 'hr' | 'holiday';
  time?: string;
}

interface CalendarDay {
  date: Date;
  currentMonth: boolean;
  today: boolean;
  events: CalendarEvent[];
}

@Component({
  selector: 'app-event-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.scss']
})
export class EventCalendarComponent {

  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  weekDaysShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  currentDate = signal(new Date());

  selectedDate = signal<Date | null>(new Date());

  events = signal<CalendarEvent[]>([
    {
      id: 1,
      title: 'Staff Meeting',
      date: '2026-06-05',
      type: 'hr',
      time: '10:00 AM'
    },
    {
      id: 2,
      title: 'Fee Review',
      date: '2026-06-09',
      type: 'finance',
      time: '11:00 AM'
    },
    {
      id: 3,
      title: 'Annual Function',
      date: '2026-06-18',
      type: 'academic',
      time: '04:00 PM'
    },
    {
      id: 4,
      title: 'Summer Vacation',
      date: '2026-06-25',
      type: 'holiday'
    }
  ]);

  monthYear = computed(() =>
    this.currentDate().toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })
  );

  calendarDays = computed(() => {

    const current = this.currentDate();

    const year = current.getFullYear();
    const month = current.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay();

    const daysInMonth =
      new Date(year, month + 1, 0).getDate();

    const daysInPrevMonth =
      new Date(year, month, 0).getDate();

    const days: CalendarDay[] = [];

    const today = new Date();

    for (let i = startDay - 1; i >= 0; i--) {

      const date = new Date(
        year,
        month - 1,
        daysInPrevMonth - i
      );

      days.push({
        date,
        currentMonth: false,
        today: false,
        events: []
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {

      const date = new Date(year, month, i);

      const eventList = this.events().filter(
        e => e.date === this.formatDate(date)
      );

      days.push({
        date,
        currentMonth: true,
        today:
          date.toDateString() === today.toDateString(),
        events: eventList
      });
    }

    while (days.length < 42) {

      const day = days.length -
        (startDay + daysInMonth) + 1;

      days.push({
        date: new Date(year, month + 1, day),
        currentMonth: false,
        today: false,
        events: []
      });
    }

    return days;
  });

  selectDay(day: CalendarDay) {
    this.selectedDate.set(day.date);
  }

  previousMonth() {
    const current = this.currentDate();

    this.currentDate.set(
      new Date(
        current.getFullYear(),
        current.getMonth() - 1,
        1
      )
    );
  }

  nextMonth() {
    const current = this.currentDate();

    this.currentDate.set(
      new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        1
      )
    );
  }

  goToday() {
    this.currentDate.set(new Date());
    this.selectedDate.set(new Date());
  }

  getSelectedEvents() {

    const selected = this.selectedDate();

    if (!selected) return [];

    return this.events().filter(
      e => e.date === this.formatDate(selected)
    );
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(
      date.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
      date.getDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  eventClass(type: string) {

    return {
      academic: 'bg-primary',
      finance: 'bg-success',
      hr: 'bg-warning',
      holiday: 'bg-danger'
    }[type] || 'bg-secondary';
  }
}
